import { Button } from "@/components/common/Button";
import Modal from "@/components/common/Modal";
import i18n from "@/i18n";
import {
  formatDateWithSeconds,
  formatDateWithSecondsDateOnly,
  formatEllipsis,
  formatNumber,
} from "@/modules/auth/utils/format";
import { useTranslation } from "react-i18next";
import { env } from "@/config/env";
import { constants } from "@/modules/calculations/consts";

interface Props {
  title: string;
  btnAmountString: string;
  usdAmountString: string;
  btnAmount: number | undefined;
  usdAmount: number | undefined;
  tonAmount: number;
  date: string;
  from?: string | null;
  to?: string | null;
  transaction?: string | undefined;
  comission: number | undefined;
  typeRecord: string;
  details: object;
  onClose: () => void;
}

const TransactionRecord = ({
  title,
  btnAmountString,
  usdAmountString,
  btnAmount,
  usdAmount,
  tonAmount,
  date,
  from,
  to,
  transaction,
  comission,
  typeRecord,
  details,
  onClose,
}: Props) => {
  const { t } = useTranslation();

  const goToTransaction = () => {
    if (env.tonNetwork === "testnet") {
      window.open(
        `https://testnet.tonviewer.com/transaction/${transaction}`,
        "_blank"
      );
    } else if (env.tonNetwork === "mainnet") {
      window.open(`https://tonviewer.com/transaction/${transaction}`, "_blank");
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <div className="w-full flex flex-col items-center p-8 pt-2 gap-5">
        <div className="w-full flex flex-col items-center gap-2 text-center">
          {Boolean(btnAmount != undefined && usdAmount != undefined) && (
            <div className="w-full flex flex-col items-start bg-skeleton rounded-lg px-4 py-3 gap-0.5">
              <p className="text-[1.25rem] leading-[1.5625rem] font-semibold">
                {btnAmountString}
              </p>
              <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
                {usdAmountString}
              </p>
            </div>
          )}

          <div className="w-full flex flex-col">
            {Boolean(usdAmount && !btnAmount) && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-[1.0625rem] leading-[1.375rem] text-hint py-3">
                    {t("history-page.modal.amount")}
                  </p>
                  <p className="text-[1.0625rem] leading-[1.375rem]">
                    {usdAmountString}
                  </p>
                </div>
                <div className="w-full pl-18">
                  <div className="w-full h-px bg-divider"></div>
                </div>
              </>
            )}
            {Boolean(btnAmount) && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-[1.0625rem] leading-[1.375rem] text-hint py-3">
                    {t("history-page.modal.amount")}
                  </p>
                  <p className="text-[1.0625rem] leading-[1.375rem]">
                    {btnAmountString}
                  </p>
                </div>
                <div className="w-full pl-18">
                  <div className="w-full h-px bg-divider"></div>
                </div>
              </>
            )}
            {Boolean(tonAmount) &&
              !(
                "transaction" in details && details.transaction === "Reinvest"
              ) && (
                <div className="flex items-center justify-between">
                  <p className="text-[1.0625rem] leading-[1.375rem] text-hint py-3">
                    {t("history-page.modal.ton_amount")}
                  </p>
                  <p className="text-[1.0625rem] leading-[1.375rem]">
                    {formatNumber(tonAmount)} TON
                  </p>
                </div>
              )}
            <div className="w-full pl-18">
              <div className="w-full h-px bg-divider"></div>
            </div>
            {Boolean(comission !== undefined && comission > 0) && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-[1.0625rem] leading-[1.375rem] text-hint py-3">
                    {t("history-page.comission")}
                  </p>
                  <p className="text-[1.0625rem] leading-[1.375rem]">
                    {formatNumber(comission || 0, {
                      precision: constants.TON_DECIMALS,
                    })}{" "}
                    TON
                  </p>
                </div>
                <div className="w-full pl-18">
                  <div className="w-full h-px bg-divider"></div>
                </div>
              </>
            )}
            <div className="flex items-center justify-between">
              <p className="text-[1.0625rem] leading-[1.375rem] text-hint py-3">
                {t("history-page.modal.time")}
              </p>
              <p className="text-[1.0625rem] leading-[1.375rem]">
                {typeRecord === "dep-reward-dailt"
                  ? formatDateWithSeconds(date, i18n.language)
                  : formatDateWithSecondsDateOnly(date, i18n.language)}
              </p>
            </div>
            {from && to && (
              <>
                <div className="w-full pl-18">
                  <div className="w-full h-px bg-divider"></div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-[1.0625rem] leading-[1.375rem] text-hint py-3">
                    {t("history-page.modal.from")}
                  </p>
                  <p className="text-[1.0625rem] leading-[1.375rem]">
                    {formatEllipsis(from)}
                  </p>
                </div>
                <div className="w-full pl-18">
                  <div className="w-full h-px bg-divider"></div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-[1.0625rem] leading-[1.375rem] text-hint py-3">
                    {t("history-page.modal.to")}
                  </p>
                  <p className="text-[1.0625rem] leading-[1.375rem]">
                    {formatEllipsis(to)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {transaction && (
          <Button
            onClick={goToTransaction}
            text={t("history-page.modal.view_transactions")}
            size="large"
            className="w-full"
          />
        )}
      </div>
    </Modal>
  );
};

export default TransactionRecord;
